// src/components/NavbarMenu.js
import React, { useMemo, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../rbac/AuthContext';
import { signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import './NavbarMenu.css';

const NAV_ITEMS = [
  { key: 'home', label: 'Home', to: '/', hash: '#', icon: 'bi-house' },
  { key: 'services', label: 'Services', to: '/#services', hash: '#services', icon: 'bi-grid' },
  { key: 'communities', label: 'Communities', to: '/#communities', hash: '#communities', icon: 'bi-buildings' },
  { key: 'why', label: 'Why Us', to: '/#why', hash: '#why', icon: 'bi-star' },
  // { key: 'features', label: 'Features', to: '/#features', hash: '#features', icon: 'bi-lightning-charge' },
  // { key: 'register', label: 'Register', to: '/#register', hash: '#register', icon: 'bi-person-plus' },
];

// Adjust these to the IDs present on your Home page:
// Added 'visitors' since it's present in the mobile bottom nav
const SECTION_IDS = ['services', 'communities', 'why', 'features', 'register', 'visitors'];

export default function NavbarMenu({
  brandName = 'Lupie',
  tagline = 'Service App',
  useRouter = false,
  onCTAClick,
}) {
  const { currentUser, role: authRole, primaryCommunityId, communities, isResident } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  // Optional: Firestore profile { displayName, role, status, primaryCommunityId, communities, ... }
  const [profile, setProfile] = useState(null);
  // Active in-page section key for highlighting (default: 'home')
  const [activeSection, setActiveSection] = useState('home');

  /* ---------- Load Firestore user profile ---------- */
  useEffect(() => {
    let canceled = false;
    const fetchProfile = async () => {
      if (!currentUser?.uid) {
        setProfile(null);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        if (!canceled) {
          setProfile(snap.exists() ? { id: snap.id, ...snap.data() } : null);

        }
      } catch (err) {
        console.error('Failed to load user profile:', err);

        if (!canceled) setProfile(null);
      }
    };
    fetchProfile();
    return () => { canceled = true; };
  }, [currentUser]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  /* ---------- Observe in-page sections for active highlight ---------- */
  const observerRef = useRef(null);

  useEffect(() => {
    // Only run observer on the Home route
    const onHome = location.pathname === '/' || location.pathname === '';
    if (!onHome) {
      setActiveSection('home');
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    const options = {
      root: null,
      rootMargin: '0px 0px -40% 0px',
      threshold: 0.3,
    };

    const callback = (entries) => {
      entries.forEach((entry) => {
        const id = entry.target.getAttribute('id');
        if (!id) return;
        if (entry.isIntersecting) {
          setActiveSection(id);
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);
    observerRef.current = observer;

    // Observe hero as "home" if present
    const hero = document.getElementById('hero') || document.querySelector('[aria-label="Hero"]');
    if (hero) {
      observer.observe(hero);
    } else {
      setActiveSection('services'); // or the first section available
    }

    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [location.pathname]);

  /* ---------- RouterLink lazy import ---------- */
  const RouterLink = useMemo(() => {
    if (!useRouter) return null;
    try {
      const { NavLink } = require('react-router-dom'); // eslint-disable-line global-require
      return NavLink;
    } catch {
      console.warn('react-router-dom is not installed. Falling back to anchor links.');
      return null;
    }
  }, [useRouter]);

  /* ---------- Helpers ---------- */
  const displayName =
    profile?.firstName ||
    currentUser?.email ||
    currentUser?.firstName ||
    'Profile';
  const empId =
    profile?.empId;
  const role = (profile?.role || 'resident').toString().toLowerCase();
  const status = (profile?.status || 'pending').toString().toLowerCase();

  // Dashboard navigation
  const goToDashboard = () => {
    // Prefer Firestore profile role if present, else AuthContext
    const effectiveRole = (profile?.role || '').toLowerCase() || (authRole || '').toLowerCase();

    // Choose a community ID
    const pid = profile?.primaryCommunityId ?? primaryCommunityId;
    const firstComm = profile?.communities?.[0] ?? (Array.isArray(communities) ? communities[0] : null);
    const communityId = pid || firstComm;

    if (effectiveRole === 'admin') {
      navigate('/admin-dashboard');
      return;
    }
    if (effectiveRole === 'employee') {
      navigate('/employee-dashboard');
      return;
    }


    if (effectiveRole === 'manager') {
      if (!communityId) {
        console.warn('Manager has no community assigned.');
        navigate('/');
        return;
      }
      navigate(`/manager/${communityId}`);
      return;
    }

    // Default → resident
    if (!communityId) {
      console.warn('Resident has no community assigned.');
      navigate('/');
      return;
    }
    navigate(`/resident/${communityId}`);
  };

  // Generic route/scroll handler
  const goToOrScroll = (target) => {
    // Handle Home
    if (target === '/' || target === '' || target === '#' || !target) {
      if (location.pathname !== '/') {
        navigate('/', { replace: false });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    // Handle in-page section like "/#services"
    if (target.startsWith('/#')) {
      const id = target.split('#')[1]; // "services"
      const scrollLater = () =>
        requestAnimationFrame(() => {
          const el = document.getElementById(id);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            // Retry briefly in case Home just mounted
            let tries = 10;
            const retry = () => {
              const el2 = document.getElementById(id);
              if (el2) el2.scrollIntoView({ behavior: 'smooth', block: 'start' });
              else if (tries--) setTimeout(retry, 50);
            };
            retry();
          }
        });

      if (location.pathname !== '/') {
        navigate('/', { replace: false });
        setTimeout(scrollLater, 0);
      } else {
        scrollLater();
      }
      return;
    }

    // Any other route (/login, /profile, etc.)
    navigate(target);
  };

  const isItemActive = (item) => {
    // Router pages
    if (useRouter) {
      if (item.to === '/') return location.pathname === '/';
      if (item.to.startsWith('/#') && (location.pathname === '/' || location.pathname === '')) {
        const key = item.to.split('#')[1];
        return activeSection === key;
      }
      return location.pathname === item.to;
    }
    // Anchor mode
    if (item.hash === '#') return activeSection === 'home';
    return item.hash === `#${activeSection}`;
  };

  const handleDefaultCTA = () => {
    const el = document.querySelector('#register');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else navigate('/register');
  };

  /* ---------- Avatar (initials) ---------- */
  const Avatar = ({ name, size = 32 }) => {
    const initials = getInitials(name);
    const bg = pickColorFromString(name || 'User');
    const style = {
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: bg,
      color: '#fff',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size < 36 ? '0.8rem' : '0.95rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      userSelect: 'none',
      lineHeight: 1,
    };
    return <span className="avatar-badge" style={style} aria-hidden="true">{initials}</span>;
  };

  function getInitials(nameOrEmail) {
    if (!nameOrEmail) return 'U';
    const s = String(nameOrEmail).trim();
    if (s.includes(' ')) {
      const [a, b] = s.split(/\s+/);
      return ((a?.[0] || '') + (b?.[0] || '')).toUpperCase() || 'U';
    }
    if (s.includes('@')) {
      return s[0].toUpperCase();
    }
    return s.slice(0, 2).toUpperCase();
  }

  // Generate a stable color from a string
  function pickColorFromString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i += 1) {
      // eslint-disable-next-line no-bitwise
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue} 65% 45%)`;
  }

  /* ---------------- Desktop Top Navbar (lg+) ---------------- */
  const DesktopTopNav = () => (
    <nav className="navbar-bg navbar navbar-expand-lg fixed-top border-bottom shadow-sm">
      {/* <div className="container"> */}
      <div className="container-fluid px-2">
        {/* Brand */}
        <a
          className="navbar-brand d-flex align-items-center gap-2"
          href="/"
          onClick={(e) => { e.preventDefault(); goToOrScroll('/'); }}
        >
          <i className="bi bi-buildings-fill text-light fs-4" aria-hidden="true"></i>
          <span className="fw-bold text-light">{brandName}</span>
          <span className="text-light">{tagline}</span>
        </a>

        {/* Desktop Menu */}
        <div className="collapse navbar-collapse show d-none d-lg-block ">
          <ul className="navbar-nav ms-auto mb-2 mb-lg-0 align-items-lg-center">
            {NAV_ITEMS.map((item) => (
              <li className="nav-item d-none d-lg-block" key={`desktop-${item.label}`}>
                <button
                  type="button"
                  className={`nav-link btn btn-link px-2 d-flex align-items-center gap-2 ${isItemActive(item) ? 'active fw-semibold text-primary' : ''}`}
                  onClick={() => goToOrScroll(
                    item.key === 'home' ? '/' : `/#${item.key}`
                  )}
                >
                  <i className={`bi ${item.icon}`} aria-hidden="true"></i>
                  {item.label}
                </button>
              </li>
            ))}

            {/* Right-side controls */}
            {!currentUser ? (
              <>
                {/* Login */}
                <li className="nav-item ms-2">
                  <button
                    type="button"
                    className="btn btn-primary-navbar-login d-inline-flex align-items-center"
                    onClick={() => goToOrScroll('/login')}
                  >
                    <i className="bi bi-box-arrow-in-right me-1" aria-hidden="true"></i>
                    Login
                  </button>
                </li>

                {/* Register Community (only when not logged in) */}
                <li className="nav-item ms-2">
                  <button
                    className="btn btn-top-navbar-reg"
                    onClick={onCTAClick ? onCTAClick : handleDefaultCTA}
                  >
                    <i className="bi bi-person-plus me-1" aria-hidden="true"></i>
                    Register
                  </button>
                </li>
              </>
            ) : (
              // Logged in: Profile dropdown
              <li className="nav-item dropdown ms-3">
                <a
                  href="#profile"
                  className="nav-link dropdown-toggle d-flex align-items-center gap-2"
                  id="profileDropdown"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  onClick={(e) => e.preventDefault()}
                >
                  <Avatar name={displayName} size={32} />
                </a>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="profileDropdown" style={{ minWidth: 260 }}>
                  {/* Header with avatar + name + role */}
                  <li className="px-3 py-2">
                    <div className="d-flex align-items-center gap-2">
                      <Avatar name={displayName} size={36} />
                      <div className="min-w-0">
                        <div className="fw-semibold text-truncate">{displayName}</div>
                        <span className={`badge rounded-pill text-bg-light border role-badge role-${empId}}`}>
                          {empId}
                        </span>
                        <span className={`badge rounded-pill text-bg-light border role-badge role-${role}`}>
                          {role}
                        </span>
                        {isResident && status !== "approved" && (
                          <span className={`badge rounded-pill text-bg-warning border role-badge role-${status}`}>
                            {status}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                  <li><hr className="dropdown-divider" /></li>

                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={goToDashboard}
                    >
                      <i className="bi bi-speedometer2"></i> Dashboard
                    </button>
                  </li>

                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => navigate('/profile')}
                    >
                      <i className="bi bi-person-badge"></i> Profile
                    </button>
                  </li>

                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button className="dropdown-item d-flex align-items-center gap-2" onClick={handleLogout}>
                      <i className="bi bi-box-arrow-right"></i> Logout
                    </button>
                  </li>
                </ul>
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );

  /* ---------------- Mobile Bottom Navbar (<lg) ---------------- */
  const MobileBottomNav = () => {
    // Local state for avatar dropup on mobile
    const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
    const mobileProfileRef = useRef(null);

    // Close dropup when clicking outside
    useEffect(() => {
      function handleDocumentClick(e) {
        if (mobileProfileRef.current && !mobileProfileRef.current.contains(e.target)) {
          setMobileProfileOpen(false);
        }
      }
      document.addEventListener('click', handleDocumentClick);
      return () => document.removeEventListener('click', handleDocumentClick);
    }, []);

    const items = [
      { key: 'home', label: 'Home', icon: 'bi-house', toOrHash: useRouter ? '/' : '#' },
      { key: 'services', label: 'Services', icon: 'bi-grid', Link: '/services' },
      { key: 'communities', label: 'Communities', icon: 'bi-buildings', toOrHash: useRouter ? '/communities' : '#communities' },
      { key: 'visitors', label: 'Visitors', icon: 'bi-qr-code', toOrHash: useRouter ? '/visitors' : '#visitors' },
    ];

    const isActiveMobile = (key, toOrHash) => {
      if (useRouter) {
        if (toOrHash === '/') return location.pathname === '/';
        if (toOrHash.startsWith('/#')) {
          const id = toOrHash.split('#')[1];
          return (location.pathname === '/' || location.pathname === '') && activeSection === id;
        }
        // Pages
        return location.pathname === toOrHash;
      }
      // Anchor mode
      if (key === 'home') return activeSection === 'home';
      return activeSection === key;
    };

    return (
      <nav
        className="d-lg-none bg-light border-top fixed-bottom"
        role="navigation"
        aria-label="Bottom navigation"
        style={{ zIndex: 1030 }}
      >
        <div className="container bottom-nav-bg">
          <ul className="nav justify-content-between py-1 flex-nowrap w-100">

            {/* Regular items */}
            {items.map((item) => (
              <li key={item.key} className="nav-item flex-fill text-center">
                <button
                  type="button"
                  className={`btn btn-link text-decoration-none ${isActiveMobile(item.key, item.toOrHash) ? 'text-dark' : 'text-light'}`}
                  onClick={() => {
                    goToOrScroll(item.key === 'home' ? '/' : `/#${item.key}`);
                    setMobileProfileOpen(false);
                  }}
                  aria-label={item.label}
                >
                  <i className={`bi ${item.icon} fs-5 d-block`} aria-hidden="true"></i>
                  <small className="d-block">{item.label}</small>
                </button>
              </li>
            ))}

            {/* Logged-in: Avatar dropup with menu */}
            {currentUser ? (
              <li
                className="nav-item dropup flex-fill text-center position-relative"
                ref={mobileProfileRef}
              >
                <button
                  type="button"
                  className={`btn btn-link text-decoration-none ${mobileProfileOpen ? 'text-dark' : 'text-light'}`}
                  onClick={() => setMobileProfileOpen((v) => !v)}
                  aria-label="Account"
                  aria-expanded={mobileProfileOpen}
                  aria-haspopup="true"
                >
                  {/* Avatar icon */}
                  <span className="d-block">
                    <Avatar name={displayName} size={24} />
                  </span>
                  <small className="d-block">Account</small>
                </button>

                {/* Dropup menu */}
                <ul
                  className={`dropdown-menu dropdown-menu-end ${mobileProfileOpen ? 'show' : ''}`}
                  style={{
                    minWidth: 260,
                    bottom: '100%',
                    // anchor above the button; allow it to overlay content
                    transform: 'translateY(-0.25rem)',
                    position: 'absolute',
                    right: 0,
                  }}
                  role="menu"
                >
                  <li className="px-3 py-2">
                    <div className="d-flex align-items-center gap-2">
                      <Avatar name={displayName} size={32} />
                      <div className="min-w-0">
                        <div className="fw-semibold text-truncate">{displayName}</div>
                        <span className={`badge rounded-pill text-bg-light border role-badge role-${role}`}>
                          {role}
                        </span>
                        {isResident && status !== "approved" && (
                          <span className={`badge rounded-pill text-bg-warning border role-badge role-${status}`}>
                            {status}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>

                  <li><hr className="dropdown-divider" /></li>

                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => { setMobileProfileOpen(false); goToDashboard(); }}
                    >
                      <i className="bi bi-speedometer2"></i> Dashboard
                    </button>
                  </li>

                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => { setMobileProfileOpen(false); navigate('/profile'); }}
                    >
                      <i className="bi bi-person-badge"></i> Profile
                    </button>
                  </li>

                  <li><hr className="dropdown-divider" /></li>

                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => { setMobileProfileOpen(false); handleLogout(); }}
                    >
                      <i className="bi bi-box-arrow-right"></i> Logout
                    </button>
                  </li>
                </ul>
              </li>
            ) : (
              // Not logged-in: Login button
              <li className="nav-item flex-fill text-center">
                <button
                  type="button"
                  className="btn btn-link text-decoration-none text-light"
                  onClick={() => goToOrScroll('/login')}
                  aria-label="Login"
                >
                  <i className="bi bi-box-arrow-in-right fs-5 d-block" aria-hidden="true"></i>
                  <small className="d-block text-light">Login</small>
                </button>
              </li>
            )}
          </ul>
        </div>

        {/* Safe-area for iOS notch (optional; uncomment if needed)
        <style>{`
          @supports(padding: env(safe-area-inset-bottom)) {
            nav.fixed-bottom {
              padding-bottom: env(safe-area-inset-bottom);
              background-clip: padding-box;
            }
          }
        `}</style>
        */}

        <style>{`
          /* Keep bottom nav on one line and distribute items evenly */
          nav.fixed-bottom .nav {
            flex-wrap: nowrap !important;
          }
          nav.fixed-bottom .nav-item {
            flex: 1 1 0 !important;   /* equal widths */
            min-width: 0 !important;  /* prevent overflow layout issues */
          }
          nav.fixed-bottom .btn {
            width: 100%;
            padding: .25rem 0 .35rem 0; /* compact vertical padding */
            line-height: 1.1;
            white-space: nowrap;        /* avoid label wrapping */
          }
          nav.fixed-bottom .btn small {
            font-size: .70rem;          /* compact label to save height */
          }
          /* Ensure mobile dropup overlays content properly */
          nav.fixed-bottom .dropdown-menu {
            z-index: 1040; /* above the bar itself */
          }
        `}</style>

      </nav>
    );
  };

  return (
    <>
      <DesktopTopNav />
      <MobileBottomNav />
      {/* Spacer so bottom nav doesn't overlap content on mobile */}
      <div className="d-lg-none" style={{ height: '10px' }} aria-hidden="true"></div>
    </>
  );
}
